import { Trans, useLingui } from "@lingui/react/macro";
import { startRegistration, WebAuthnError } from "@simplewebauthn/browser";
import { type ReactNode, useEffect, useEffectEvent, useState } from "react";

import type { TOTPEnrollBeginResponse } from "../../../api";

import { ErrorCode } from "../../../api";
import {
  ApiConnector,
  apiErrorCode,
  RequestError,
} from "../../../api/api-connector";
import { errorText } from "../../../app/error-text";
import { maybeReportError } from "../../../app/errors";
import { Button } from "../../../components/ui-kit/button";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { Spinner } from "../../../components/ui-kit/spinner";
import { BackupCodes } from "./backup-codes";
import { useCodeErrorText } from "./code-error";
import { MethodChoice } from "./method-choice";
import {
  isAlreadyRegistered,
  isDismissedPrompt,
  usePasskeyPrompt,
} from "./passkey";
import { TotpEnrollment } from "./totp-enrollment";

type Step =
  /** `failure` is why the last attempt didn't work, if it didn't. */
  | { kind: "choice"; failure?: Failure }
  | { kind: "loading" }
  | { kind: "totp"; enrollment: TOTPEnrollBeginResponse }
  /**
   * The browser's passkey prompt is open — or, once `saving`, has returned a
   * passkey the backend is now storing.
   */
  | { kind: "passkey"; saving?: boolean }
  | { kind: "backupCodes"; codes: string[] }
  | { kind: "expired" }
  /** Enrollment already went through — a first attempt whose reply was lost. */
  | { kind: "alreadyEnrolled" }
  /** Too many wrong codes — the backend stopped taking them. */
  | { kind: "exhausted" }
  /** The account is exempt from two-factor and may not enroll a method. */
  | { kind: "unavailable" }
  | { kind: "failed"; failure: Failure };

/** An attempt that didn't work, and which method it was for. */
interface Failure {
  method: "totp" | "passkey";
  error: unknown;
}

/**
 * Setting up a second factor, in both places it happens: mandatory enrollment
 * at login (with a `challengeId`, since there is no session yet) and a
 * voluntary opt-in (without one). The steps are the same; the wording and what
 * finishing means come from the caller.
 *
 * Without a `method`, the user first picks between a passkey and an
 * authenticator app.
 */
export function MfaEnrollment({
  api,
  challengeId,
  method,
  reason,
  intro,
  expiredMessage,
  onEnrolled,
  onCancel,
  cancelLabel,
}: {
  api: ApiConnector;
  challengeId?: string;
  method?: "totp" | "passkey";
  /** Why the user is setting two-factor up, shown with the choice of method. */
  reason?: ReactNode;
  /** Shown above the authenticator app's QR code. */
  intro?: ReactNode;
  expiredMessage: ReactNode;
  onEnrolled: () => void;
  onCancel: () => void;
  cancelLabel: ReactNode;
}) {
  const { t } = useLingui();
  const codeErrorText = useCodeErrorText();
  const offersChoice = method === undefined;
  const [step, setStep] = useState<Step>(
    offersChoice
      ? { kind: "choice" }
      : method === "passkey"
        ? { kind: "passkey" }
        : { kind: "loading" },
  );
  const [codeError, setCodeError] = useState<unknown>();
  // Bumped to remount the setup screen, which is how a rejected code is cleared.
  const [attempt, setAttempt] = useState(0);
  const passkeyPrompt = usePasskeyPrompt();

  async function startTotpEnrollment() {
    setStep({ kind: "loading" });

    try {
      const enrollment = await api.startTotpEnrollment(challengeId);
      setStep({ kind: "totp", enrollment });
    } catch (error) {
      if (endedTheEnrollment(error)) {
        return;
      }

      // A fault, not a dead challenge — so keep the retry in reach.
      maybeReportError(error);
      showFailure({ method: "totp", error });
    }
  }

  async function enrollPasskey() {
    const isCurrent = passkeyPrompt.start();
    setStep({ kind: "passkey" });

    try {
      // Fetched only now, not ahead of time: the backend keeps the passkey
      // challenge only as long as the browser's prompt may stay open.
      const options = await api.startPasskeyEnrollment(challengeId);
      if (!isCurrent()) {
        return;
      }

      const credential = await startRegistration({ optionsJSON: options });
      if (!isCurrent()) {
        return;
      }

      // No cancelling past this point: once the backend has the passkey, its
      // backup codes must be shown.
      setStep({ kind: "passkey", saving: true });
      const { backup_codes } = await api.confirmPasskeyEnrollment(
        challengeId,
        credential,
      );

      finish(backup_codes);
    } catch (error) {
      if (!isCurrent()) {
        return;
      }

      // A rejected passkey is a 401, which would otherwise read as an expired
      // login.
      const rejected = apiErrorCode(error) === ErrorCode.MFA_INVALID_CODE;

      if (!rejected && endedTheEnrollment(error)) {
        return;
      }

      // A dismissed prompt, or a passkey the device already holds, is the
      // user's situation. Anything else the browser refuses — a domain
      // mismatch, say — is a fault worth hearing about.
      if (!isDismissedPrompt(error) && !isAlreadyRegistered(error)) {
        maybeReportError(error);
      }

      // Nothing is lost: the user can try again, or pick the other method.
      showFailure({ method: "passkey", error });
    }
  }

  function cancelPasskey() {
    passkeyPrompt.cancel();

    if (offersChoice) {
      setStep({ kind: "choice" });
    } else {
      onCancel();
    }
  }

  /** Back to the choice if there is one, otherwise to the retry screen. */
  function showFailure(failure: Failure) {
    setStep(
      offersChoice ? { kind: "choice", failure } : { kind: "failed", failure },
    );
  }

  // Without a choice to make, enrollment starts right away: the challenge is
  // only valid for a few minutes, so a "get started" screen would eat into it.
  // Once, when the screen opens — not again whenever the caller re-renders.
  const startRightAway = useEffectEvent(() => {
    if (offersChoice) {
      return;
    }

    void (method === "passkey" ? enrollPasskey() : startTotpEnrollment());
  });

  useEffect(() => {
    startRightAway();
  }, []);

  async function confirm(secret: string, code: string) {
    setCodeError(undefined);

    try {
      const { backup_codes } = await api.confirmTotpEnrollment(
        challengeId,
        secret,
        code,
      );

      finish(backup_codes);
    } catch (error) {
      maybeReportError(error);

      // A rejected code leaves the secret bound, so the QR code still stands.
      // Only the code is cleared — a TOTP code is good for one time step.
      if (apiErrorCode(error) === ErrorCode.MFA_INVALID_CODE) {
        setAttempt((previous) => previous + 1);
        setCodeError(error);
        return;
      }

      if (endedTheEnrollment(error)) {
        return;
      }

      setCodeError(error);
    }
  }

  function finish(backupCodes: string[]) {
    if (backupCodes.length > 0) {
      setStep({ kind: "backupCodes", codes: backupCodes });
    } else {
      // No codes to show — the user still holds the ones from an earlier
      // enrollment, so there is nothing left to do here.
      onEnrolled();
    }
  }

  /**
   * Move a failure that ends the enrollment onto the step that says so.
   *
   * @returns whether the enrollment is over, leaving nothing to retry.
   */
  function endedTheEnrollment(error: unknown) {
    switch (apiErrorCode(error)) {
      // The attempt budget is spent. It is kept per account as well as per
      // challenge, so starting over won't hand out a fresh one either.
      case ErrorCode.RATE_LIMIT_EXCEEDED:
        setStep({ kind: "exhausted" });
        return true;

      // Exempt accounts are refused outright. `/mfa/status` doesn't say which
      // they are, so the offer to set two-factor up is made before we can know.
      case ErrorCode.PERMISSION_DENIED:
        setStep({ kind: "unavailable" });
        return true;

      // The account already holds a method, so a confirm that looked like it
      // failed had in fact gone through. Only reachable at login.
      case ErrorCode.TOKEN_ALREADY_USED:
        setStep({ kind: "alreadyEnrolled" });
        return true;
    }

    // Any other 401 means the binding is gone — the secret lapsed, or the
    // challenge did. Either way nothing on screen can still be confirmed.
    if (error instanceof RequestError && error.response.status === 401) {
      setStep({ kind: "expired" });
      return true;
    }

    return false;
  }

  /** Why the enrollment is over. Only the expired wording is the caller's. */
  function endMessage(
    kind: "expired" | "alreadyEnrolled" | "exhausted" | "unavailable",
  ) {
    switch (kind) {
      case "expired":
        return expiredMessage;

      case "alreadyEnrolled":
        return (
          <Trans>
            Two-factor authentication is already set up on this account. Please
            log in.
          </Trans>
        );

      case "exhausted":
        return (
          <Trans>
            Too many failed attempts. Please wait a few minutes and start again.
          </Trans>
        );

      case "unavailable":
        return (
          <Trans>
            Two-factor authentication is not available for this account.
          </Trans>
        );
    }
  }

  function failureText(failure: Failure) {
    return failure.method === "passkey"
      ? passkeyFailureText(failure.error)
      : errorText(failure.error);
  }

  function passkeyFailureText(error: unknown) {
    if (isAlreadyRegistered(error)) {
      return t`This device already has a passkey for your account.`;
    }

    if (error instanceof WebAuthnError) {
      return offersChoice
        ? t`No passkey was created. Try again, or use an authenticator app instead.`
        : t`No passkey was created. Please try again.`;
    }

    if (apiErrorCode(error) === ErrorCode.MFA_INVALID_CODE) {
      return t`That passkey couldn't be verified. Please try again.`;
    }

    return errorText(error);
  }

  switch (step.kind) {
    case "choice":
      return (
        <MethodChoice
          reason={reason}
          errorMessage={step.failure ? failureText(step.failure) : ""}
          onPasskey={enrollPasskey}
          onAuthenticatorApp={startTotpEnrollment}
          onCancel={onCancel}
          cancelLabel={cancelLabel}
        />
      );

    case "loading":
      return (
        <div className="flex justify-center">
          <Spinner />
        </div>
      );

    case "passkey":
      return (
        <div
          className="flex flex-col items-center gap-4"
          data-testid="mfa-passkey-setup"
        >
          <Spinner />
          <p className="text-sm text-center max-w-80">
            <Trans>
              Follow your browser's instructions to create a passkey.
            </Trans>
          </p>
          <Button disabled={step.saving} onClick={cancelPasskey}>
            <Trans>Cancel</Trans>
          </Button>
        </div>
      );

    case "totp":
      return (
        <TotpEnrollment
          key={attempt}
          enrollment={step.enrollment}
          intro={intro}
          errorMessage={codeError ? codeErrorText(codeError) : ""}
          onConfirm={(code) => confirm(step.enrollment.secret, code)}
          onCancel={onCancel}
          cancelLabel={cancelLabel}
        />
      );

    case "backupCodes":
      return <BackupCodes codes={step.codes} onContinue={onEnrolled} />;

    case "failed":
      return (
        <div className="flex flex-col items-center">
          <ErrorMessage message={failureText(step.failure)} />
          <div className="flex gap-2 mt-4">
            <Button
              variant="primary"
              onClick={
                step.failure.method === "passkey"
                  ? enrollPasskey
                  : startTotpEnrollment
              }
            >
              <Trans>Try again</Trans>
            </Button>
            <Button onClick={onCancel}>{cancelLabel}</Button>
          </div>
        </div>
      );

    case "expired":
    case "alreadyEnrolled":
    case "exhausted":
    case "unavailable":
      return (
        <div className="flex flex-col items-center">
          <ErrorMessage message={endMessage(step.kind)} />
          <Button className="mt-4" onClick={onCancel}>
            {cancelLabel}
          </Button>
        </div>
      );
  }
}
